import React, { forwardRef, useRef, useImperativeHandle, useEffect } from 'react';

export default forwardRef(function GetPaginaOnce(props, ref) {
  const { logoSrc } = props || {};
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);

  // small helper to wrap & draw text, returns last y
  const drawWrappedText = (ctx, text, x, y, maxWidth, lineHeight) => {
    if (!text) return y;
    const words = String(text).split(/\s+/);
    let line = '';
    for (let n = 0; n < words.length; n++) {
      const testLine = line ? (line + ' ' + words[n]) : words[n];
      const metrics = ctx.measureText(testLine);
      if (metrics.width > maxWidth && line) {
        ctx.fillText(line, x, y);
        line = words[n];
        y += lineHeight;
      } else {
        line = testLine;
      }
    }
    if (line) {
      ctx.fillText(line, x, y);
      y += lineHeight;
    }
    return y;
  };

  const draw = (opts = {}) => {
    return new Promise((resolve) => {
      const canvas = canvasRef.current;
      const wrapper = wrapperRef.current;
      if (!canvas) return resolve();

      const containerWidth = wrapper ? wrapper.clientWidth : 800;
      const width = Math.max(600, containerWidth);
      const height = Math.round(width * (8.5 / 11));
      const dpr = window.devicePixelRatio || 1;

      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      const ctx = canvas.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, width, height);

      // background
      ctx.fillStyle = opts.bgColor || props.bgColor || '#ffffffff';
      ctx.fillRect(0, 0, width, height);

      const padding = 24;
      let y = padding;

      // left title square
      const titleFontSize = opts.titleFontSize || 45;
      const squareSize = typeof opts.titleSquareSize === 'number' ? opts.titleSquareSize : Math.round(titleFontSize * 1.5);
      const squareGap = typeof opts.titleSquareGap === 'number' ? opts.titleSquareGap : 25;
      const squareText = opts.titleSquareText || '3';

      ctx.fillStyle = opts.titleSquareBg || '#f1f891ff';
      ctx.fillRect(padding, y, squareSize, squareSize);

      ctx.fillStyle = opts.titleSquareColor || '#111111ff';
      ctx.font = `bold ${Math.round(squareSize * 0.55)}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(squareText, padding + squareSize / 2, y + squareSize / 2);

      // title (to the right of square)
      const title = opts.title || props.title || 'Advertencias de posibles riesgos';
      const titleX = padding + squareSize + squareGap;
      const titleMaxW = Math.min(typeof opts.titleMaxWidth === 'number' ? opts.titleMaxWidth : 900, width - titleX - padding);
      ctx.fillStyle = opts.titleColor || '#111827';
      ctx.font = `bold ${titleFontSize}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';

      // draw title with wrapping helper
      const titleLineHeight = Math.round(titleFontSize * 1.05);
      const drawWrapped = (context, text, x, startY, maxW, lh) => {
        if (!text) return startY;
        const words = String(text).split(/\s+/);
        let line = '';
        let curY = startY;
        for (let i = 0; i < words.length; i++) {
          const test = line ? (line + ' ' + words[i]) : words[i];
          if (context.measureText(test).width > maxW && line) {
            context.fillText(line, x, curY);
            line = words[i];
            curY += lh;
          } else {
            line = test;
          }
        }
        if (line) {
          context.fillText(line, x, curY);
          curY += lh;
        }
        return curY;
      };

      const titleYStart = y;
      y = drawWrapped(ctx, title, titleX, titleYStart, titleMaxW, titleLineHeight);

      // leave gap after title before box
      y += 200;

      // subtitle drawn above the box; blue bordered box contains only texto
      const subTitle = opts.subTitle || props.subTitle;
      const texto = opts.texto || props.texto;
      if (subTitle || texto) {
        const boxInnerMaxW = Math.min(width - padding * 8, 1200);
        // wrap text preserving explicit newlines (\n)
        const wrapLines = (context, txt, maxW) => {
          if (!txt) return [];
          return String(txt)
            .split(/\r?\n/) // split into paragraphs by newline
            .map(paragraph => {
              if (!paragraph) return ['']; // preserve blank line
              const words = paragraph.split(/\s+/);
              const lines = [];
              let line = '';
              for (let i = 0; i < words.length; i++) {
                const test = line ? (line + ' ' + words[i]) : words[i];
                if (context.measureText(test).width > maxW && line) {
                  lines.push(line);
                  line = words[i];
                } else {
                  line = test;
                }
              }
              if (line) lines.push(line);
              return lines;
            })
            .flat();
        };

        // subtitle lines (drawn above box)
        const subFontSize = opts.subTitleFontSize || 40;
        ctx.font = `600 ${subFontSize}px Inter, Arial, sans-serif`;
        const subLineH = Math.round(subFontSize * 1.25);
        const subLines = wrapLines(ctx, subTitle, boxInnerMaxW);

        // texto lines (inside box)
        const bodyFontSize = opts.textoFontSize || 35;
        ctx.font = `${bodyFontSize}px Inter, Arial, sans-serif`;
        const bodyLineH = Math.round(bodyFontSize * 1.4);
        const textLines = wrapLines(ctx, texto, boxInnerMaxW);

        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';

        // draw subtitle inside a blue rounded box with white text
        if (subLines.length) {
          ctx.font = `600 ${subFontSize}px Inter, Arial, sans-serif`;
          // measure widest line
          let maxTextW = 0;
          for (let i = 0; i < subLines.length; i++) {
            maxTextW = Math.max(maxTextW, ctx.measureText(subLines[i]).width);
          }

          const padX = 20;
          const padY = 10;
          const boxW = Math.min(Math.round(maxTextW + padX * 2), width - padding * 4);
          const boxX = Math.round((width - boxW) / 2);
          const boxY = y;
          const boxTextHeight = subLines.length * subLineH;
          const boxH = boxTextHeight + padY * 2;
          const r = 8;

          // box fill (blue) and border (darker blue)
          ctx.beginPath();
          ctx.moveTo(boxX + r, boxY);
          ctx.lineTo(boxX + boxW - r, boxY);
          ctx.quadraticCurveTo(boxX + boxW, boxY, boxX + boxW, boxY + r);
          ctx.lineTo(boxX + boxW, boxY + boxH - r);
          ctx.quadraticCurveTo(boxX + boxW, boxY + boxH, boxX + boxW - r, boxY + boxH);
          ctx.lineTo(boxX + r, boxY + boxH);
          ctx.quadraticCurveTo(boxX, boxY + boxH, boxX, boxY + boxH - r);
          ctx.lineTo(boxX, boxY + r);
          ctx.quadraticCurveTo(boxX, boxY, boxX + r, boxY);
          ctx.closePath();
          ctx.fillStyle = opts.subBoxFill || '#2b6cb0';
          ctx.fill();
          ctx.lineWidth = opts.subBoxBorderWidth || 2;
          ctx.strokeStyle = opts.subBoxBorderColor || '#1e40af';
          ctx.stroke();

          // draw white subtitle text centered
          ctx.fillStyle = opts.subTitleColor || '#ffffff';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'top';
          let textY = boxY + padY;
          const centerTextX = boxX + boxW / 2;
          ctx.font = `600 ${subFontSize}px Inter, Arial, sans-serif`;
          for (let i = 0; i < subLines.length; i++) {
            ctx.fillText(subLines[i], centerTextX, textY);
            textY += subLineH;
          }

          y = boxY + boxH + 12; // advance below subtitle box
        }

        // box metrics and draw (contains texto)
        const vPadding = 18;
        const hPadding = 16;
        const contentHeight = (textLines.length * bodyLineH);
        const boxHeight = contentHeight + vPadding * 2;
        const boxWidth = Math.min(boxInnerMaxW + hPadding * 2, width - padding * 4);
        const boxX = Math.round((width - boxWidth) / 2);
        const boxY = Math.max(y, padding + squareSize + 12);

        const r = 8;
        ctx.beginPath();
        ctx.moveTo(boxX + r, boxY);
        ctx.lineTo(boxX + boxWidth - r, boxY);
        ctx.quadraticCurveTo(boxX + boxWidth, boxY, boxX + boxWidth, boxY + r);
        ctx.lineTo(boxX + boxWidth, boxY + boxHeight - r);
        ctx.quadraticCurveTo(boxX + boxWidth, boxY + boxHeight, boxX + boxWidth - r, boxY + boxHeight);
        ctx.lineTo(boxX + r, boxY + boxHeight);
        ctx.quadraticCurveTo(boxX, boxY + boxHeight, boxX, boxY + boxHeight - r);
        ctx.lineTo(boxX, boxY + r);
        ctx.quadraticCurveTo(boxX, boxY, boxX + r, boxY);
        ctx.closePath();

        ctx.fillStyle = opts.boxFill || 'rgba(255,255,255,0.95)';
        ctx.fill();
        ctx.lineWidth = opts.boxBorderWidth || 2;
        ctx.strokeStyle = opts.boxBorderColor || '#2b6cb0';
        ctx.stroke();

        // draw texto justified inside box
        let curTextY = boxY + vPadding;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        if (textLines.length) {
          ctx.fillStyle = opts.textoColor || '#0f172a';
          ctx.font = `${bodyFontSize}px Inter, Arial, sans-serif`;
          const textStartX = boxX + hPadding;
          const textMaxW = boxWidth - hPadding * 2;

          const drawJustifiedLine = (context, line, x, y, maxW) => {
            const trimmed = String(line || '').trim();
            if (!trimmed) {
              context.fillText('', x, y);
              return;
            }

            const words = trimmed.split(/\s+/);
            if (words.length <= 1) {
              context.fillText(trimmed, x, y);
              return;
            }

            const lineWidth = context.measureText(trimmed).width;
            const extra = maxW - lineWidth;
            if (extra <= 0) {
              context.fillText(trimmed, x, y);
              return;
            }

            const gaps = words.length - 1;
            const extraPerGap = extra / gaps;
            let drawX = x;
            for (let i = 0; i < words.length; i++) {
              const w = words[i];
              context.fillText(w, drawX, y);
              if (i < gaps) {
                const baseGap = context.measureText(' ').width;
                const wordWidth = context.measureText(w).width;
                drawX += wordWidth + baseGap + extraPerGap;
              }
            }
          };

          for (let i = 0; i < textLines.length; i++) {
            const line = textLines[i] || '';
            const isLastLine = i === textLines.length - 1;
            const isBlank = !line.trim();

            if (isBlank || isLastLine) {
              ctx.fillText(line, textStartX, curTextY);
            } else {
              drawJustifiedLine(ctx, line, textStartX, curTextY, textMaxW);
            }
            curTextY += bodyLineH;
          }
        }
        y = boxY + boxHeight + 12;
      }

      // optional logo (bottom-right)
      const mainLogoSrc = opts.logoSrc || logoSrc;
      if (mainLogoSrc) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          try {
            const maxW = Math.min(200, Math.round(width * 0.12));
            const w = Math.min(maxW, img.width);
            const h = (img.height / img.width) * w;
            const lx = width - padding - w;
            const ly = height - padding - h;
            ctx.drawImage(img, lx, ly, w, h);
          } catch (e) { /* ignore */ }
          resolve({ canvas, width, height });
        };
        img.onerror = () => resolve({ canvas, width, height });
        img.src = mainLogoSrc;
      } else {
        resolve({ canvas, width, height });
      }
    });
  };

  const download = (filename) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = filename || `pagina_nueve_${new Date().toISOString().slice(0,10)}.png`;
    a.click();
  };

  useImperativeHandle(ref, () => ({
    draw,
    download,
    getDataUrl: () => {
      const canvas = canvasRef.current;
      return canvas ? canvas.toDataURL('image/png') : null;
    }
  }), [draw]);

  useEffect(() => {
    draw();
    const onResize = () => draw();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={wrapperRef} style={{ width: '100%' }}>
      <canvas ref={canvasRef} style={{ width: '100%', display: 'block', borderRadius: 6 }} />
    </div>
  );
});